/* eslint-disable react-hooks/rules-of-hooks */
import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';
import * as yup from 'yup';
import { FormFieldProps } from 'lib/forms/FormFieldProps';
import { Field, FormikErrors, FormikTouched, FormikValues, useFormikContext } from 'formik';
import { useTheme } from 'lib/context/ThemeContext';
import { FormFieldsTheme } from './FormFields.Theme';
import classNames from 'classnames';
import { getValidatonSchema, getValueProviderValue } from 'lib/forms/FormFieldUtils';
import FieldWrapper from '../Structure/FieldWrapper/FieldWrapper';
import { ChangeEvent, useState } from 'react';
import React from 'react';

export type OpenTextAreaProps = Feature.EnterpriseWeb.Enterprise.Forms.Fields.TextArea &
  FormFieldProps;

export const getInitialValue = (
  props: OpenTextAreaProps,
  additionalDetails?: Record<string, unknown> | undefined
) => {
  const valueProvider = getValueProviderValue(props, additionalDetails);
  if (valueProvider) {
    return valueProvider;
  } else if (props?.fields?.defaultValue) {
    return props?.fields?.defaultValue?.value || '';
  }
};

export const getValidationSchema = (props: OpenTextAreaProps, schema: yup.AnyObject) => {
  const { validations } = props?.fields;
  if (validations) {
    const validator = getValidatonSchema('string', props);
    if (validator) {
      schema[props?.fields?.fieldName?.value] = (validator as yup.StringSchema).trim();
    }
  }
  return schema;
};

const OpenTextArea = (props: OpenTextAreaProps): JSX.Element => {
  let formikContext;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    formikContext = useFormikContext<FormikValues>();
  } catch (error) {
    console.log('Error accessing Formik context:', error);
    return <React.Fragment />; // Avoid rendering if formik context is unavailable
  }
  const userType = formikContext.values['user-type'];

  const { themeData } = useTheme(FormFieldsTheme);
  const { errors, touched, values, handleChange } = useFormikContext<FormikValues>();

  const maxLength = props.fields.maxLength?.value;
  const minLength = props.fields.minLength?.value;

  const [remainingChar, setRemainingChar] = useState(
    parseInt(maxLength) - values[props?.fields?.fieldName?.value]?.length
  );

  const isInvalid =
    (touched as FormikTouched<FormikValues>)[props?.fields?.fieldName?.value] &&
    (errors as FormikErrors<FormikValues>)[props?.fields?.fieldName?.value];
  if (userType === 'Homeowner' || userType === '') {
    return <React.Fragment />; // Hide component if user-type is not "Trade Professional"
  }
  return (
    <FieldWrapper {...props}>
      <Field
        id={props.id}
        name={props?.fields?.fieldName?.value}
        placeholder={props.fields?.placeholderText?.value}
        rows={props.fields?.rows?.value}
        minLength={minLength}
        maxLength={maxLength}
        as="textarea"
        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
          if (props?.fields?.showRemainingCharactersCount?.value) {
            setRemainingChar(parseInt(maxLength) - e.target.value.length);
          }
          handleChange(e);
        }}
        className={classNames(
          themeData.classes.textarea,
          isInvalid ? themeData.classes.errorOutline : '',
          values[props?.fields?.fieldName?.value] ? 'border-black' : 'border-gray'
        )}
      />

      {props?.fields?.showRemainingCharactersCount?.value && maxLength && (
        <div className={themeData.classes.textareaCharCount}>
          {remainingChar === parseInt(maxLength)
            ? `${remainingChar} characters allowed`
            : `${remainingChar} characters remaining`}
        </div>
      )}
    </FieldWrapper>
  );
};

export default OpenTextArea;
